import { useContext, useEffect, useState } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as SupportIcon } from 'assets/support.svg';
import i18nContext from 'components/i18n-context';
import {
  ACCOUNT_PROPERTIES,
  ACCOUNT_TOOLTIP_TEXT,
  CUSTOMER_PERMISSIONS,
  DATA_LAYER_EVENTS,
  WALLESTER_ACCOUNT_TOOLTIP_TEXT
} from 'components/common/constants';
import AccountDetails from 'components/common/AccountDetails';
import { ContactUs } from 'components/common/ContactUs/ContactUs';
import ComplianceRequests from 'components/common/LatestTransactions/ComplianceRequests';
import LatestPaymentObligations from 'components/common/LatestPaymentObligations/LatestPaymentObligations';
import LatestTransactionsCompact from 'components/common/LatestTransactions/LatestTransactionsCompact';
import NoOutlet from 'components/common/NoOutlet/NoOutlet';
import PaymentForm from 'components/common/PaymentForm';
import Verification from 'components/Verification/Verification';
import { ROUTE_PATHS } from 'routes/constants';
import { pushDataLayerEvent } from 'services/analytics';
import {
  getCustomerId,
  getCustomerType,
  getInternalUserId,
  getIsSuccessLoginStatus,
  removeIsSuccessLoginStatus
} from 'services/authUtils';
import { isCryptoProvider, isWallesterProvider, isManualTypeProvider } from 'services/utils';
import { Card } from 'uikit/Card/Card';
import './DashBoard.scss';

const DashBoard = ({ userStore, paymentStore, transactionStore, cardsStore, paymentObligationsStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const [activeAccountDetails, setActiveAccountDetails] = useState(null);

  useEffect(() => {
    if (getIsSuccessLoginStatus()) {
      pushDataLayerEvent(DATA_LAYER_EVENTS.LOGIN_SUCCESS, {
        user_id_internal: getInternalUserId(),
        customer_id: getCustomerId(),
        customer_type: getCustomerType()
      });
      removeIsSuccessLoginStatus();
    }
  }, []);

  useEffect(() => {
    if (userStore.userData.account?.account_number) {
      transactionStore.getComplianceRequestList(userStore.userData.account?.account_number);
      paymentObligationsStore.getPaymentObligationsList(userStore.userData.account?.account_number);
      userStore.getUserAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.userData.account?.account_number]);

  const openAccountDetails = (account) => {
    setActiveAccountDetails(account);
  };

  const goToTransaction = (account) => {
    transactionStore.setAccount(account);
    transactionStore.setIsRedirectFromAccountCard(true);
    navigate(ROUTE_PATHS.TRANSACTIONS);
  };

  const goToPayment = (account) => {
    paymentStore.setSelectedAccount(account);
    navigate(ROUTE_PATHS.NEW_PAYMENT);
  };

  const goToCards = (account) => {
    cardsStore.setSelectedAccount(account);
    navigate(ROUTE_PATHS.CARDS);
  };

  if (userStore.userData?.account && userStore.isCustomerVerified !== null && !userStore.isCustomerVerified) {
    return (
      <section className={'right-section verifications'}>
        <Verification />
      </section>
    );
  }

  if (!userStore.userData?.account) {
    return <NoOutlet error={userStore.error} />;
  }

  const isShowPaymentByPermission = userStore?.currentCustomerPermissions
    ? userStore?.currentCustomerPermissions?.find(
        (permission) => permission === CUSTOMER_PERMISSIONS.CREATE_TRANSACTION
      )
    : true;

  // Render full-screen AccountDetails instead of the dashboard when active
  if (activeAccountDetails) {
    return (
      <section className={'right-section dashboard'}>
        <AccountDetails accountData={activeAccountDetails} setActiveAccountDetails={setActiveAccountDetails} />
      </section>
    );
  }

  return (
    <>
      {userStore.userAccounts.length === 0 ? (
        <ContactUs
          icon={<SupportIcon />}
          header={i18n.getMessage('dashboard.noAccounts.title')}
          description={i18n.getMessage('dashboard.noAccounts.description')}
          button={i18n.getMessage('dashboard.noAccounts.button')}
        />
      ) : (
        <section className={'page-container dashboard'}>
          <div className={'dashboard-accounts-wrapper'}>
            <div className={'dashboard-header'}>
              <p className={'dashboard-header-text'}>{i18n.getMessage('dashboard.accounts.header.text')}</p>
              <Link className={'link-normal view-all-accounts-link'} to={ROUTE_PATHS.ACCOUNTS}>
                {i18n.getMessage('dashboard.button.viewAll')}
              </Link>
            </div>
            <div className={'card-wrapper'}>
              {userStore.userAccounts
                ?.slice(0, userStore.userAccounts?.length >= 4 ? 3 : userStore.userAccounts.length)
                .map((account) => {
                  const isWallesterAccount = isWallesterProvider(account);
                  const isManualAccount = isManualTypeProvider(account);
                  const isCryptoAccount = isCryptoProvider(account?.transfer_provider);

                  return (
                    <Card
                      key={account[ACCOUNT_PROPERTIES.ACCOUNT_NUMBER]}
                      type={'card'}
                      isCardAccount={isWallesterAccount}
                      isManualAccount={isManualAccount}
                      isCryptoAccount={isCryptoAccount}
                      account={account}
                      balance={account[ACCOUNT_PROPERTIES.AVAILABLE]}
                      balanceDetails={account?.availableDetails}
                      reservedDetails={account?.onHoldDetails}
                      currency={account[ACCOUNT_PROPERTIES.CURRENCY]}
                      networkCode={account[ACCOUNT_PROPERTIES.NETWORK]}
                      reserved={account[ACCOUNT_PROPERTIES.ON_HOLD]}
                      buttonText={'Review account'}
                      // onExclaimPointClick={() => showPopup(account)}
                      onExclaimPointClick={() => openAccountDetails(account)}
                      onTransactionClick={() => goToTransaction(account)}
                      onPaymentClick={isWallesterAccount ? () => goToCards(account) : () => goToPayment(account)}
                      tooltipText={isWallesterAccount ? WALLESTER_ACCOUNT_TOOLTIP_TEXT : ACCOUNT_TOOLTIP_TEXT}
                    />
                  );
                })}
            </div>
          </div>
          {process.env.REACT_APP_IS_PAYFORM_ON_MAIN === 'true' && isShowPaymentByPermission && <PaymentForm />}
          {transactionStore.complianceRequestList.length > 0 && <ComplianceRequests />}
          {paymentObligationsStore.obligationList.length > 0 && <LatestPaymentObligations />}
          <LatestTransactionsCompact />
        </section>
      )}
    </>
  );
};

DashBoard.propTypes = {
  userStore: MobXPropTypes.observableObject,
  paymentStore: MobXPropTypes.observableObject,
  transactionStore: MobXPropTypes.observableObject,
  cardsStore: MobXPropTypes.observableObject,
  paymentObligationsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  userStore: stores.userStore,
  paymentStore: stores.paymentStore,
  transactionStore: stores.transactionsStore,
  cardsStore: stores.cardsStore,
  paymentObligationsStore: stores.paymentObligationsStore
}))(observer(DashBoard));
